import React, { useCallback, useContext, useMemo } from "react";
import { Commodity, CommodityAgentLink, CommodityClaimScopeLink, CommodityCountryLink, CommodityFamilyLink } from "./products";
import { useCrud, useLinkedCrud } from "../BackendProvider";
import { commodity_agent_link, commodity_claim_scope_link, commodity_country_link, commodity_family_link } from "../data";
import _ from "lodash";
import { useQueryClient } from "@tanstack/react-query";

const ProductsContext = React.createContext({
    commodities: [] as Commodity[],
    postCommodity: (commodity: Commodity) => Promise.resolve(commodity),
    deleteCommodity: (commodity: Commodity) => Promise.resolve({}),
    commodityById: {} as Record<number, Commodity>,
    commodityByReference: {} as Record<string, Commodity>,

    commodityFamilyLinks: [] as CommodityFamilyLink[],
    postCommodityFamilyLink: (links: CommodityFamilyLink | CommodityFamilyLink[]) => Promise.resolve(links) as Promise<CommodityFamilyLink | CommodityFamilyLink[]>,
    deleteCommodityFamilyLink: (links: CommodityFamilyLink | CommodityFamilyLink[]) => Promise.resolve({}),
    commodityClaimScopeLinks: [] as CommodityClaimScopeLink[],
    postCommodityClaimScopeLink: (links: CommodityClaimScopeLink | CommodityClaimScopeLink[]) => Promise.resolve(links) as Promise<CommodityClaimScopeLink | CommodityClaimScopeLink[]>,
    deleteCommodityClaimScopeLink: (links: CommodityClaimScopeLink | CommodityClaimScopeLink[]) => Promise.resolve({}),
    commodityCountryLinks: [], // TODO
    commodityCountryLinksByCommodityId: {} as Record<number, CommodityCountryLink[]>,
    postCommodityCountryLink: (links: CommodityCountryLink | CommodityCountryLink[]) => Promise.resolve(links) as Promise<CommodityCountryLink | CommodityCountryLink[]>,
    deleteCommodityCountryLink: (links: CommodityCountryLink | CommodityCountryLink[]) => Promise.resolve({}),

    commodityAgents: [] as CommodityAgentLink[],
    commodityAgentsByCommodityId: {} as Record<number, CommodityAgentLink[]>,
    postCommodityAgent: (links: CommodityAgentLink | CommodityAgentLink[]) => Promise.resolve(links) as Promise<CommodityAgentLink | CommodityAgentLink[]>,
    deleteCommodityAgent: (links: CommodityAgentLink | CommodityAgentLink[]) => Promise.resolve({}),

    commoditiesByFamilyId: {} as Record<number, (Commodity & CommodityFamilyLink)[]>,
    commoditiesByClaimScopeId: {} as Record<number, (Commodity & CommodityClaimScopeLink)[]>,

    isLoading: false as boolean,
    reload: () => {},
})

export function ProductsProvider({children}) {
    const queryClient = useQueryClient()

    const {data: commodities, postMutation: postCommodity, deleteMutation: deleteCommodity, isLoading: isCommoditiesLoading} =
        useCrud<Commodity>('commodity', c => c.commodityId)

    const {data: commodityFamilyLinks, postMutation: postCommodityFamilyLink, deleteMutation: deleteCommodityFamilyLink, isLoading: isFamilyLinksLoading} =
        useLinkedCrud<CommodityFamilyLink>(commodity_family_link, (a, b) => a.commodityId === b.commodityId && a.patentFamilyId === b.patentFamilyId)
    const {data: commodityClaimScopeLinks, postMutation: postCommodityClaimScopeLink, deleteMutation: deleteCommodityClaimScopeLink, isLoading: isClaimScopesLoading} =
        useLinkedCrud<CommodityClaimScopeLink>(commodity_claim_scope_link, (a, b) => a.commodityId === b.commodityId && a.claimScopeId === b.claimScopeId)
    const {data: commodityCountryLinks, postMutation: postCommodityCountryLink, deleteMutation: deleteCommodityCountryLink, isLoading: isCountriesLoading} =
        useLinkedCrud<CommodityCountryLink>(commodity_country_link, (a, b) => a.commodityId === b.commodityId && a.countryCode === b.countryCode)
    const {data: commodityAgents, postMutation: postCommodityAgent, deleteMutation: deleteCommodityAgent, isLoading: isAgentsLoading} =
        useLinkedCrud<CommodityAgentLink>(commodity_agent_link, (a, b) => a.commodityId === b.commodityId && a.agentId === b.agentId)

    const commodityById = useMemo(() => _.keyBy(commodities, c => c.commodityId), [commodities])
    const commodityByReference = useMemo(() => _.keyBy(commodities, 'commodityReference'), [commodities])
    const commodityCountryLinksByCommodityId = useMemo(() => _.groupBy(commodityCountryLinks, 'commodityId'), [commodityCountryLinks])
    const commodityAgentsByCommodityId = useMemo(() => _.groupBy(commodityAgents, 'commodityId'), [commodityAgents])

    // includes third party; does not include protected; does not include via claim scopes
    const commoditiesByFamilyId = useMemo(
        () => _(commodityFamilyLinks)
            .filter(l => l.protected)
            .map(l => ({ ...commodityById[l.commodityId], ...l }))
            .filter(c => c.commodityReference !== undefined)
            .groupBy(cl => cl.patentFamilyId)
            .value(),
        [commodityFamilyLinks, commodityById],
    )

    // includes third party; does not include protected; does not include via family
    const commoditiesByClaimScopeId = useMemo(
        () => _(commodityClaimScopeLinks)
            .filter(l => l.protected)
            .map(l => ({ ...commodityById[l.commodityId], ...l }))
            .filter(c => c.commodityReference !== undefined)
            .groupBy(cl => cl.claimScopeId)
            .value(),
        [commodityClaimScopeLinks, commodityById],
    )

    const isLoading = isCommoditiesLoading || isFamilyLinksLoading || isClaimScopesLoading || isCountriesLoading || isAgentsLoading

    const reload = useCallback(() => {
        queryClient.invalidateQueries({queryKey: ['commodity']})
        queryClient.invalidateQueries({queryKey: [commodity_family_link]})
        queryClient.invalidateQueries({queryKey: [commodity_claim_scope_link]})
        queryClient.invalidateQueries({queryKey: [commodity_country_link]})
        queryClient.invalidateQueries({queryKey: [commodity_agent_link]})
    }, [queryClient])

    const value = useMemo(() => ({
        commodities,
        postCommodity, deleteCommodity,
        commodityById, 
        commodityByReference,
        commodityFamilyLinks,
        postCommodityFamilyLink, deleteCommodityFamilyLink,
        commodityClaimScopeLinks,
        postCommodityClaimScopeLink, deleteCommodityClaimScopeLink,
        commodityCountryLinks, commodityCountryLinksByCommodityId,
        postCommodityCountryLink, deleteCommodityCountryLink,
        commodityAgents, commodityAgentsByCommodityId,
        postCommodityAgent, deleteCommodityAgent,
        commoditiesByFamilyId, commoditiesByClaimScopeId,
        isLoading, reload,
    }), [
        commodities,
        postCommodity,
        deleteCommodity,
        commodityById,
        commodityByReference,
        commodityFamilyLinks,
        postCommodityFamilyLink,
        deleteCommodityFamilyLink,
        commodityClaimScopeLinks,
        postCommodityClaimScopeLink,
        deleteCommodityClaimScopeLink,
        commodityCountryLinks,
        commodityCountryLinksByCommodityId,
        postCommodityCountryLink,
        deleteCommodityCountryLink,
        commodityAgents,
        commodityAgentsByCommodityId,
        postCommodityAgent,
        deleteCommodityAgent,
        commoditiesByFamilyId,
        commoditiesByClaimScopeId,
        isLoading,
        reload,
    ])

    return (
        <ProductsContext.Provider value={value}>
            {children}
        </ProductsContext.Provider>
    )
}

export function useProducts() {
    return useContext(ProductsContext)
}
